import React, { useMemo, useState } from "react";
import { formatKm, universeClass, universeLabel } from "./towerFormat";
import Card from "../ui/Card";
import TowerDriverDialog from "./TowerDriverDialog";
import TowerFocusMap, { FOCUS_COLORS } from "./TowerFocusMap";
import { activateOnKey } from "./towerShared";
import { statusText, statusTone } from "./shipmentMarkers";
import { driversNear, NEARBY_KM, openUnassigned } from "./locateDrivers";
import { useTowerDay } from "./useTowerDay";
import type { TowerDashboard, TowerListedShipment } from "../../types/controlTower";

// Pestaña "Sin asignar": por cada envío sin chofer, los choferes que hoy
// trabajaron en su localidad o cerca (posibles responsables). Solo sugiere.
export default function TowerUnassigned({
  data,
  today,
  token,
  operationalDateLabel,
  countLabel,
}: {
  data: TowerDashboard;
  today: string;
  token: string | null;
  operationalDateLabel: string;
  countLabel: string;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [candidateId, setCandidateId] = useState<string | null>(null);
  // Se guarda el id: el detalle abierto toma cada actualización del día.
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const { day, setDay, data: source, error } = useTowerDay(today, data, token);
  const drivers = useMemo(() => source?.drivers || [], [source]);
  const openDriver = openDriverId ? drivers.find((d) => d.id === openDriverId) || null : null;
  const pending = useMemo(() => openUnassigned(drivers), [drivers]);
  const selected = pending.find((s) => s.id === selectedId) || pending[0] || null;
  const candidates = useMemo(() => {
    if (!selected) return [];
    const target: TowerListedShipment = { ...selected, assignedTo: "Sin asignar", driverId: "__unassigned__" };
    return driversNear(drivers, target);
  }, [drivers, selected]);
  const candidate = candidates.find((c) => c.driver.id === candidateId) || candidates[0] || null;
  const pick = (id: string) => {
    setSelectedId(id);
    setCandidateId(null);
  };

  return (
    <Card className="tower-board">
      <div className="tower-board-heading">
        <h2>
          Envíos sin asignar <span className="tower-muted">{pending.length}</span>
        </h2>
        <label className="tower-inline-date">
          Día
          <input
            type="date"
            className="tower-date"
            value={day}
            max={today}
            onChange={(e) => {
              setDay(e.target.value);
              setSelectedId(null);
              setCandidateId(null);
            }}
          />
        </label>
      </div>
      {error && (
        <div className="tower-error" role="alert">
          {error}
        </div>
      )}
      {!source && !error && (
        <p className="tower-muted" role="status">
          Cargando…
        </p>
      )}
      {source && pending.length === 0 && (
        <p className="tower-empty">No hay envíos sin asignar {day === today ? "hoy" : "ese día"}.</p>
      )}
      {selected && (
        <div className="tower-geo-layout">
          <div className="tower-geo-list">
            {pending.map((s) => (
              <div
                key={s.id}
                className={`tower-geo-item tower-unassigned-item${selected.id === s.id ? " selected" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => pick(s.id)}
                onKeyDown={activateOnKey(() => pick(s.id))}
              >
                <div className="tower-geo-item-body">
                  <strong className="tower-unassigned-client">{s.client}</strong>
                  <div className="tower-geo-chips">
                    <span className={`tower-geo-chip ${universeClass(s.shippingUniverse)}`}>
                      {universeLabel(s.shippingUniverse).toUpperCase()}
                    </span>
                    <span className={`tower-state ${s.status === "pending" ? "unassigned" : statusTone(s)}`}>
                      {statusText(s)}
                    </span>
                    {s.zone !== "Sin clasificar" && <span className="tower-geo-chip">{s.zone}</span>}
                  </div>
                  <span>{[s.address, s.locality].filter(Boolean).join(", ") || "Sin dirección"}</span>
                  <small>
                    {s.tracking} · {operationalDateLabel} {s.operationalDate || "sin fecha"}
                  </small>
                </div>
              </div>
            ))}
          </div>
          <div className="tower-geo-detail">
            <div className="tower-geo-detail-head">
              <div>
                <span className="tower-eyebrow">Posibles responsables</span>
                <h3>
                  {selected.tracking} · {selected.locality || "Sin localidad"}
                </h3>
              </div>
              <div className="tower-geo-legend">
                <span>
                  <i style={{ background: FOCUS_COLORS.target }} />
                  Envío sin asignar
                </span>
                <span>
                  <i style={{ background: FOCUS_COLORS.route }} />
                  Reparto del chofer
                </span>
              </div>
            </div>
            <div className="tower-table-scroll">
              <table className="tower-table">
                <thead>
                  <tr>
                    <th>Chofer</th>
                    <th>Distancia</th>
                    <th>Misma localidad</th>
                    <th>{countLabel}</th>
                    <th>En camino</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr
                      key={c.driver.id}
                      className={`tower-driver-row${candidate?.driver.id === c.driver.id ? " tower-row-selected" : ""}`}
                      tabIndex={0}
                      onClick={() => setCandidateId(c.driver.id)}
                      onKeyDown={activateOnKey(() => setCandidateId(c.driver.id))}
                    >
                      <td>
                        <strong>{c.driver.name}</strong>
                        <small>{c.driver.zones.filter((z) => z !== "Sin clasificar").join(" / ")}</small>
                      </td>
                      <td>{formatKm(c.nearestKm)}</td>
                      <td>{c.sameLocality}</td>
                      <td>{c.driver.counts.total}</td>
                      <td>{c.driver.counts.inTransit}</td>
                      <td>
                        <button
                          type="button"
                          className="tower-link-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDriverId(c.driver.id);
                          }}
                        >
                          Ver reparto →
                        </button>
                      </td>
                    </tr>
                  ))}
                  {candidates.length === 0 && (
                    <tr>
                      <td colSpan={6} className="tower-empty">
                        Ningún chofer trabajó en esa localidad ni a {NEARBY_KM} km o menos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="tower-unassigned-map">
              <TowerFocusMap target={selected} others={candidate?.driver.shipments || []} targetLabel="Sin asignar" />
            </div>
          </div>
        </div>
      )}
      {openDriver && (
        <TowerDriverDialog
          key={`${day}:${openDriver.id}`}
          driver={openDriver}
          date={day}
          operationalDateLabel={operationalDateLabel}
          countLabel={countLabel}
          onClose={() => setOpenDriverId(null)}
        />
      )}
    </Card>
  );
}
